import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'

/**
 * Afișează factura SPV (PDF) a unei comenzi. Aducem PDF-ul ca blob (Bearer din interceptor — URL
 * direct/iframe nu primesc token). Pe DESKTOP îl arătăm inline în iframe. Pe MOBIL browserele nu
 * randează PDF în iframe și nici deschiderea în tab nou a blob-ului nu e de încredere, deci oferim
 * doar DESCĂRCAREA (anchor cu `download`, gest de click autentic → merge peste tot).
 */
export function SpvInvoiceViewer({
  inboundId,
  orderId,
  pdfPath,
  docId,
  onClose,
}: {
  inboundId: number
  /**
   * Comanda din care se deschide factura. Cand e dat, luam PDF-ul prin ruta comenzii
   * (`orders:read`) — altfel agentii primesc 403, fiindca ruta din eFactura cere
   * `anaf:read`, permisiune pe care rolul „vanzari" nu o are.
   */
  orderId?: number
  /** Ruta PDF-ului, cand ecranul are una proprie (cu drepturile lui). Are prioritate. */
  pdfPath?: string
  docId?: string | null
  onClose: () => void
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const filename = `${(docId ?? `factura-spv-${inboundId}`).replace(/[^\w.-]/g, '_')}.pdf`

  useEffect(() => {
    let revoke: string | null = null
    setUrl(null)
    setErr(null)
    const path =
      pdfPath ??
      (orderId != null ? `/orders/${orderId}/spv-invoice/pdf` : `/anaf/messages/${inboundId}/pdf`)
    api
      .get(path, { responseType: 'blob', timeout: 60_000 })
      .then((resp) => {
        const u = URL.createObjectURL(resp.data as Blob)
        revoke = u
        setUrl(u)
      })
      .catch(async (e: unknown) => {
        // Cererea are `responseType: 'blob'`, deci si raspunsul de EROARE vine ca Blob:
        // `response.data.error.message` e mereu undefined si ramaneam cu mesajul generic
        // („Nu am putut incarca factura"), care ascundea un simplu 403. Citim blob-ul.
        const resp = (e as { response?: { data?: unknown; status?: number } })?.response
        let msg: string | null = null
        const data = resp?.data
        if (data instanceof Blob) {
          try {
            const parsed = JSON.parse(await data.text()) as { error?: { message?: string } }
            msg = parsed.error?.message ?? null
          } catch {
            /* nu era JSON — ramanem pe mesajul generic */
          }
        } else {
          msg = (data as { error?: { message?: string } } | undefined)?.error?.message ?? null
        }
        setErr(msg ?? `Factura nu s-a putut încărca${resp?.status ? ` (${resp.status})` : ''}`)
      })
    return () => {
      if (revoke) URL.revokeObjectURL(revoke)
    }
  }, [inboundId, orderId, pdfPath])

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={docId ? `Factură ${docId}` : 'Factură SPV'}
      footer={
        <>
          {url && (
            <>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded bg-b4l-blue px-3 py-2 text-sm font-medium text-white transition hover:bg-b4l-blue-dark"
              >
                Deschide factura
              </a>
              <a
                href={url}
                download={filename}
                className="inline-flex items-center justify-center gap-2 rounded border border-b4l-border bg-white px-3 py-2 text-sm font-medium text-b4l-text transition hover:bg-b4l-bg"
              >
                Descarcă
              </a>
            </>
          )}
          <Button variant="secondary" onClick={onClose}>
            Închide
          </Button>
        </>
      }
    >
      {err ? (
        <div className="p-4 text-sm text-b4l-red-dark">{err}</div>
      ) : url ? (
        <>
          {/* Desktop: PDF inline în iframe */}
          <iframe
            src={url}
            title="Factură SPV"
            className="hidden h-[72vh] w-full rounded border border-b4l-border sm:block"
          />
          {/* Mobil: iframe-ul nu randează PDF → deschidere în tab */}
          <div className="flex h-[30vh] items-center justify-center px-4 text-center text-sm text-b4l-text-muted sm:hidden">
            Apasă <strong className="mx-1">„Deschide factura”</strong> (jos) ca s-o vezi.
          </div>
        </>
      ) : (
        <div className="flex h-[30vh] items-center justify-center text-sm text-b4l-text-muted">
          Se încarcă factura…
        </div>
      )}
    </Modal>
  )
}
